import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Slide,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, ChevronLeftRounded, QrCode, Remove } from '@mui/icons-material';
import { decryptURL } from 'utils/functions';
import { ArmazenagemContext } from 'contexts/ArmazenagemContext';
import { DropsContext } from 'contexts/DropsContext';
import Container from 'components/Container';
import Button from 'components/Button';
import Input from 'components/Input';
import Card from 'components/Card';
import localeText from 'utils/localeText';
import styles from './styles';
import moment from 'moment';

const Modal = ({ row, open, onSubmit, inputRef, handleCloseModal }) => {
  const inputRefModal = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current.blur();
        inputRefModal.current.focus();
      }, 500);
    }
  }, [open]);

  const Transition = forwardRef((props, ref) => (
    <Slide direction="up" ref={ref} {...props} />
  ));

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      TransitionComponent={Transition}
      PaperProps={{ sx: { minWidth: '50%' } }}
    >
      <DialogTitle>Digite a quantidade que deseja transferir</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} />
          <Grid item xs={12}>
            <Input inputRef={inputRefModal} label="Quantidade" type="number" />
            <Typography variant="caption" color="secondary">
              Máximo permitido: {row?.quantidade || 0} {row?.um || ''}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            onSubmit(inputRefModal.current.valueAsNumber);
            handleCloseModal();
          }}
        >
          Salvar
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleCloseModal}
        >
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Itens = ({ documentos, loading, bip1, setBip1 }) => {
  const { drops } = useContext(DropsContext);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  const handleBlur = () => {
    inputRef.current.focus();
  };

  const handleChange = (e) => setBip1(e?.target?.value);

  return (
    <Card>
      <Grid container spacing={2}>
        <Grid item xs={12} display="flex" alignItems="center" gap={5}>
          <QrCode sx={styles?.qrcode} color="primary" />
          <Typography variant="h6" flex={1}>
            Faça a leitura do que deseja armazenar
          </Typography>
        </Grid>
        <Grid item xs={12} sx={styles?.scroll} textAlign="center">
          {Boolean(documentos?.length) ? (
            documentos?.map((item, idx) => (
              <Box key={idx?.toString()} mt={2}>
                <Typography variant="h6" textAlign="start">
                  Nota / Fatura {item?.documento || ''}
                </Typography>
                {Boolean(item?.fornecedor) && (
                  <Typography fontSize={14} textAlign="start">
                    {item?.fornecedor}
                  </Typography>
                )}
                <DataGrid
                  rows={item?.itens}
                  columns={[
                    {
                      field: 'referencia',
                      headerName: 'Referência',
                      width: 150,
                      sortable: false,
                    },
                    {
                      field: 'descricao',
                      headerName: 'Produto',
                      minWidth: 400,
                      flex: 1,
                      sortable: false,
                    },
                    {
                      field: 'variacao',
                      headerName: 'Variação',
                      width: 150,
                      sortable: false,
                    },
                    {
                      field: 'validade',
                      headerName: 'Validade',
                      width: 150,
                      sortable: false,
                      valueFormatter: ({ value }) =>
                        Boolean(value)
                          ? moment(value)?.format('DD/MM/YYYY')
                          : '',
                    },
                    {
                      field: 'rastreabilidade',
                      headerName: 'Rastreabilidade',
                      width: 150,
                      sortable: false,
                    },
                    {
                      field: 'armazem',
                      headerName: 'Armazenado',
                      width: 300,
                      type: 'number',
                      sortable: false,
                      renderCell: ({ row }) => {
                        const arr = row?.Separados || [];
                        return (
                          <Box flex={1} display="flex" flexDirection="column">
                            {arr?.map((a) => {
                              const endereco = a?.endereco || '';
                              const endereco1 = a?.endereco1 || '';
                              const endereco2 = a?.endereco2 || '';

                              return (
                                <Box
                                  flex={1}
                                  display="flex"
                                  justifyContent="space-between"
                                >
                                  <Typography variant="body2" fontWeight="bold">
                                    {endereco}
                                    {endereco1 ? ` -> ${endereco1}` : ''}
                                    {endereco2 ? ` -> ${endereco2}` : ''}
                                  </Typography>
                                  <Typography variant="body2">
                                    {a?.quantidade || 0} {row?.um || ''}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      },
                    },
                  ]}
                  hideFooter
                  autoHeight
                  getRowHeight={() => 'auto'}
                  density="compact"
                  loading={loading}
                  disableColumnMenu
                  showCellRightBorder
                  showColumnRightBorder
                  disableSelectionOnClick
                  localeText={localeText}
                  components={{ LoadingOverlay: LinearProgress }}
                />
              </Box>
            ))
          ) : loading ? (
            <LinearProgress />
          ) : (
            <Typography variant="caption">Nenhum item encontrado</Typography>
          )}
        </Grid>
      </Grid>
      <input
        ref={inputRef}
        value={bip1}
        onBlur={handleBlur}
        onChange={handleChange}
        style={styles?.hideInput}
      />
    </Card>
  );
};

const Produtos = ({
  bip2,
  setBip2,
  loading,
  produtos,
  selected,
  handleEdit,
  aninhamento,
  isModalOpen,
  handleOpenModal,
  handleCloseModal,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  const handleChange = (e) => setBip2(e?.target?.value);

  return (
    <Card>
      <Grid container spacing={5}>
        <Grid item xs={12} display="flex" alignItems="center" gap={5}>
          <QrCode sx={styles?.qrcode} color="primary" />
          <Box>
            <Typography variant="h6" flex={1}>
              Faça a leitura de onde deseja armazenar os itens abaixo
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {aninhamento && (
            <Typography variant="caption" color="error" fontWeight="bold">
              Os itens abaixo estão localizados no endereço{' '}
              {produtos?.find((f) => f?.endereco)?.endereco}
            </Typography>
          )}
          <DataGrid
            rows={produtos}
            columns={[
              {
                field: 'referencia',
                headerName: 'Referência',
                width: 150,
                sortable: false,
              },
              {
                field: 'descricao',
                headerName: 'Produto',
                minWidth: 400,
                flex: 1,
                sortable: false,
              },
              {
                field: 'variacao',
                headerName: 'Variação',
                width: 150,
                sortable: false,
              },
              {
                field: 'rastreabilidade',
                headerName: 'Rastreabilidade',
                width: 150,
                sortable: false,
              },
              {
                field: 'validade',
                headerName: 'Validade',
                width: 150,
                sortable: false,
                valueFormatter: ({ value }) =>
                  Boolean(value) ? moment(value)?.format('DD/MM/YYYY') : '',
              },
              ...(!aninhamento
                ? [
                    {
                      field: '_quantidade',
                      headerName: 'Quantidade',
                      headerAlign: 'left',
                      type: 'number',
                      width: 200,
                      sortable: false,
                      renderCell: ({ row, id, field }) => (
                        <Box
                          flex={1}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleEdit({
                                id,
                                field,
                                value: row?._quantidade - 1,
                              })
                            }
                            disabled={row?._quantidade <= 0}
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                          <Typography
                            variant="body2"
                            sx={styles?.txtqtd}
                            onClick={() => handleOpenModal(row)}
                          >
                            {row?._quantidade || 0} {row?.um || ''}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleEdit({
                                id,
                                field,
                                value: row?._quantidade + 1,
                              })
                            }
                            disabled={row?._quantidade >= row?.quantidade}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </Box>
                      ),
                    },
                  ]
                : [
                    {
                      field: '_quantidade',
                      headerName: 'Quantidade',
                      type: 'number',
                      width: 200,
                      sortable: false,
                      renderCell: ({ row }) => (
                        <Typography variant="body2">
                          {row?._quantidade || 0} {row?.um || ''}
                        </Typography>
                      ),
                    },
                  ]),
            ]}
            hideFooter
            autoHeight
            density="compact"
            disableSelectionOnClick
            getRowHeight={() => 'auto'}
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            localeText={localeText}
            loading={loading}
            components={{ LoadingOverlay: LinearProgress }}
          />
        </Grid>
      </Grid>
      <input
        ref={inputRef}
        value={bip2}
        style={styles?.hideInput}
        onBlur={() => {
          if (!isModalOpen) {
            inputRef.current.focus();
          }
        }}
        onChange={handleChange}
      />
      <Modal
        row={selected}
        open={isModalOpen}
        inputRef={inputRef}
        handleCloseModal={() => handleCloseModal(inputRef)}
        onSubmit={(value) =>
          handleEdit({ id: selected?.id, field: '_quantidade', value })
        }
      />
    </Card>
  );
};

const Armazenagem = () => {
  const navigate = useNavigate();
  const { hash } = useParams();
  const lotes = decryptURL(hash);
  const [tab, setTab] = useState(0);
  const [bip1, setBip1] = useState('');
  const [bip2, setBip2] = useState('');
  const [produtos, setProdutos] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    getLoading,
    postLoading,
    getEtiqueta,
    getArmazenagem,
    postArmazenagem,
  } = useContext(ArmazenagemContext);
  const aninhamento = produtos?.some((s) => Boolean(s?.aninhamento));

  const fetchData = ({ data = [] }) => {
    const agrupado = data?.reduce((acc, item) => {
      const existingDocumento = acc.find(
        (grupo) => grupo.id === item.documento_id
      );

      if (existingDocumento) {
        existingDocumento.itens.push(item);
      } else {
        acc.push({
          id: item?.documento_id,
          documento: item?.documento,
          fornecedor: item?.fornecedor,
          itens: [item],
        });
      }
      return acc;
    }, []);

    setDocumentos(agrupado);
  };

  const loadData = () => {
    const params = { lotes: lotes?.map((l) => l?.id) };
    getArmazenagem({ params, cb: (data) => fetchData({ data }) });
  };

  const handleSubmit = () => {
    const data = {
      ...JSON.parse(bip2),
      etiqueta: JSON.parse(bip1),
      itens:
        produtos
          ?.filter((f) => Boolean(f?._quantidade))
          ?.map((p) => ({ id: p?.id, quantidade: p?._quantidade })) || [],
    };
    const cbError = () => setBip2('');

    postArmazenagem({ data, cb: handleCancel, cbError });
  };

  const handleCancel = () => {
    setTab(0);
    setBip1('');
    setBip2('');
    setProdutos([]);
  };

  const handleEdit = ({ id, field, value }) => {
    const editedRows = produtos?.map((p) => {
      if (id === p?.id) {
        if (field === '_quantidade' && value <= 0) {
          return { ...p, [field]: 0 };
        } else if (field === '_quantidade' && value > p?.quantidade) {
          return { ...p, [field]: p?.quantidade };
        }
        return { ...p, [field]: value };
      }
      return p;
    });
    setProdutos(editedRows);
  };

  const handleOpenModal = (row) => {
    setSelected(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = (reference) => {
    setSelected(null);
    setIsModalOpen(false);
    if (reference?.current) {
      reference.current.focus();
    }
  };

  const props = {
    bip1,
    bip2,
    setBip1,
    setBip2,
    selected,
    produtos,
    documentos,
    handleEdit,
    isModalOpen,
    aninhamento,
    handleCancel,
    handleOpenModal,
    handleCloseModal,
    loading: getLoading || postLoading,
  };

  useEffect(() => {
    if (!bip1) return;

    const timeout = setTimeout(() => {
      const etiqueta = bip1;
      const params = { etiqueta, lotes: lotes?.map((l) => l?.id) };
      const cb = (data) => {
        setProdutos(
          data?.map((m) => ({ _quantidade: m?.quantidade || 0, ...m }))
        );
        setTab(1);
      };
      const cbError = () => setBip1('');
      getEtiqueta({ params, cb, cbError });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [bip1]);

  useEffect(() => {
    if (!bip2) return;

    const timeout = setTimeout(() => {
      handleSubmit();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [bip2]);

  useEffect(() => {
    if (tab === 0) {
      loadData();
    }
  }, [tab]);

  const RenderTabs = () => {
    switch (tab) {
      case 0:
        return <Itens {...props} />;
      case 1:
        return <Produtos {...props} />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <Box sx={styles?.headerContainer}>
        <IconButton
          size="small"
          color="primary"
          onClick={() => (Boolean(tab) ? handleCancel() : navigate(-1))}
          sx={styles?.backBtn}
        >
          <ChevronLeftRounded />
        </IconButton>
        <Typography variant="h5" sx={styles?.title}>
          Armazenagem
        </Typography>
      </Box>
      <RenderTabs />
    </Container>
  );
};

export default Armazenagem;
